import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const EXTRUDE_3D_PARAMS = [
  {
    kind: 'float',
    key: 'depth',
    label: 'Extrusionstiefe',
    min: 1,
    max: 400,
    step: 1,
    scale: 'log',
    default: 60,
    unit: 'px',
    bulkRange: [15, 180],
    group: 'Geometrie',
  },
  {
    kind: 'float',
    key: 'bevel',
    label: 'Fasenbreite',
    min: 0,
    max: 30,
    step: 0.5,
    default: 4,
    unit: 'px',
    group: 'Geometrie',
  },
  {
    kind: 'int',
    key: 'bevelSegments',
    label: 'Fasensegmente',
    min: 1,
    max: 8,
    step: 1,
    default: 2,
    group: 'Geometrie',
    showIf: (v) => (v.bevel as number) > 0,
  },

  {
    kind: 'float',
    key: 'azimuth',
    label: 'Azimut',
    min: -180,
    max: 180,
    step: 1,
    default: 26,
    unit: '°',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'elevation',
    label: 'Höhenwinkel',
    min: -85,
    max: 85,
    step: 1,
    default: 22,
    unit: '°',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'distance',
    label: 'Distanz',
    min: 0.5,
    max: 6,
    step: 0.05,
    default: 1.8,
    unit: '×',
    group: 'Kamera',
  },
  {
    kind: 'float',
    key: 'fov',
    label: 'Blickwinkel',
    min: 8,
    max: 110,
    step: 1,
    default: 38,
    unit: '°',
    group: 'Kamera',
  },

  {
    kind: 'enum',
    key: 'material',
    label: 'Material',
    default: 'matte',
    group: 'Material',
    options: [
      { value: 'matte', label: 'Matt' },
      { value: 'metal', label: 'Metall' },
      { value: 'glass', label: 'Glas' },
      { value: 'toon', label: 'Toon' },
      { value: 'wireframe', label: 'Wireframe' },
      { value: 'normal', label: 'Normalen' },
    ],
  },
  {
    kind: 'color',
    key: 'color',
    label: 'Grundfarbe',
    default: '#d8ff3e',
    group: 'Material',
    showIf: (v) => v.material !== 'normal',
  },
  {
    kind: 'float',
    key: 'metalness',
    label: 'Metalness',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.15,
    group: 'Material',
    showIf: (v) => v.material === 'matte' || v.material === 'metal',
  },
  {
    kind: 'float',
    key: 'roughness',
    label: 'Roughness',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.42,
    group: 'Material',
    showIf: (v) => v.material === 'matte' || v.material === 'metal',
  },

  {
    kind: 'float',
    key: 'keyIntensity',
    label: 'Hauptlicht',
    min: 0,
    max: 8,
    step: 0.05,
    default: 2.6,
    group: 'Licht',
  },
  {
    kind: 'color',
    key: 'keyColor',
    label: 'Hauptlichtfarbe',
    default: '#ffffff',
    group: 'Licht',
  },
  {
    kind: 'float',
    key: 'fillIntensity',
    label: 'Fülllicht',
    min: 0,
    max: 6,
    step: 0.05,
    default: 0.9,
    group: 'Licht',
  },
  {
    kind: 'color',
    key: 'fillColor',
    label: 'Fülllichtfarbe',
    default: '#4a7cff',
    group: 'Licht',
  },
  {
    kind: 'float',
    key: 'ambient',
    label: 'Umgebungslicht',
    min: 0,
    max: 3,
    step: 0.05,
    default: 0.55,
    group: 'Licht',
  },
  {
    kind: 'float',
    key: 'lightAzimuth',
    label: 'Lichtrichtung',
    min: -180,
    max: 180,
    step: 1,
    default: -40,
    unit: '°',
    group: 'Licht',
  },

  {
    kind: 'bool',
    key: 'shadows',
    label: 'Schattenwurf',
    default: true,
    group: 'Szene',
  },
  {
    kind: 'bool',
    key: 'floor',
    label: 'Bodenebene',
    default: false,
    group: 'Szene',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Szene',
  },
] as const satisfies ParamSchema;

export type Extrude3dValues = SchemaValues<typeof EXTRUDE_3D_PARAMS>;
